import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { Button, Card, DropdownMenu, DropdownMenuItem, IconButton, Row } from 'components/Blocks';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon } from 'components/Icons';

import { SingleProperty } from './SingleProperty';

import { demographicsRenderer } from 'pages/Projects/components';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { useKeyPress } from 'hooks';

import { getLastIndex } from 'utils/array';
import { getCountryLabel } from 'utils/countries';

export function ProjectDemographics() {
  const { project } = useProjectProfile();
  const { t } = useLang();

  const [countryIndex, setCountryIndex] = useState(0);

  const nextCountry = () => {
    setCountryIndex((old) => {
      const newIndex = old + 1;

      return project.countries[newIndex] ? newIndex : 0;
    });
  };

  const previousCountry = () => {
    setCountryIndex((old) => {
      const newIndex = old - 1;

      return project.countries[newIndex] ? newIndex : getLastIndex(project.countries);
    });
  };

  const country = project.countries[countryIndex];

  const multiCountry = project.countries.length > 1;

  useKeyPress('ArrowRight', () => multiCountry && nextCountry());
  useKeyPress('ArrowLeft', () => multiCountry && previousCountry());

  return (
    <Card flow="column" gap="6">
      <Row
        justifyContent="space-between"
        className="rounded-md bg-muted px-2 py-1 font-medium text-primary"
      >
        {multiCountry && (
          <IconButton size="small" onClick={previousCountry} tooltipContent="Press ←">
            <ChevronLeftIcon />
          </IconButton>
        )}

        <DropdownMenu
          trigger={
            <Button
              className="mx-auto w-fit disabled:opacity-100"
              size="small"
              variant="ghost"
              disabled={project.countries.length === 1}
            >
              {getCountryLabel(country)}

              {project.countries.length > 1 && <ChevronDownIcon className="ml-auto" size={16} />}
            </Button>
          }
        >
          {project.countries.map((projectCountry) => (
            <DropdownMenuItem
              key={projectCountry}
              onClick={() => setCountryIndex(project.countries.indexOf(projectCountry))}
            >
              {getCountryLabel(projectCountry)}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>

        {multiCountry && (
          <IconButton size="small" onClick={nextCountry} tooltipContent="Press →">
            <ChevronRightIcon />
          </IconButton>
        )}
      </Row>

      <div className="grid grid-cols-2 gap-4 phone:grid-cols-1">
        {demographicsRenderer.map(({ getLabel, key, Icon }) => (
          <SingleProperty
            key={key}
            Icon={Icon}
            label={getLabel(t)}
            value={project.demographics?.[country]?.[key] || 'N/A'}
          />
        ))}
      </div>

      <SingleProperty
        Icon={UsersIcon}
        label={t('quotas')}
        value={
          project.demographics[country]?.quota ? project.demographics[country].quotaDetails : 'N/A'
        }
      />
    </Card>
  );
}
